/**
 * Çoklu dil altyapısı.
 *
 * Diller: tr (varsayılan/fallback), en, cnr (Karadağca).
 *
 * Dil seçici YALNIZCA çok dilli alan adlarında görünür:
 *   admin.hrtakip.com · employee.hrtakip.com
 * Diğer alan adları (hrtakip.com, app.hrtakip.com) Türkçe çalışır.
 * Kural tek yerde tanımlıdır: config/host.config.ts → isI18nHost()
 *
 * Yerel geliştirmede VITE_ENABLE_I18N=true ile açılabilir.
 */
import { createI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import 'dayjs/locale/tr'
import 'dayjs/locale/en'
import 'dayjs/locale/me'

import tr from '@/locales/tr.json'
import en from '@/locales/en.json'
import cnr from '@/locales/cnr.json'
import { isI18nHost } from '@/config/host.config'

export const SUPPORTED_LOCALES = ['tr', 'en', 'cnr'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'tr'
const STORAGE_KEY = 'hr-portal.locale'

/**
 * Dil seçici görünürlüğü — çalışma zamanında alan adına göre belirlenir.
 * Böylece tüm alan adları tek imajdan sunulur; ayrı build gerekmez.
 */
export const isI18nEnabled = isI18nHost()

/** Kullanıcıya gösterilecek dil listesi. */
export const LOCALE_OPTIONS: Array<{ value: AppLocale; label: string; flag: string }> = [
  { value: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'cnr', label: 'Crnogorski', flag: '🇲🇪' },
]

/** dayjs locale karşılıkları (Karadağca için dayjs 'me' locale'i kullanılır). */
const DAYJS_LOCALE: Record<AppLocale, string> = {
  tr: 'tr',
  en: 'en',
  cnr: 'me',
}

/** Intl / toLocaleDateString için BCP-47 karşılıkları. */
const BCP47: Record<AppLocale, string> = {
  tr: 'tr-TR',
  en: 'en-GB',
  cnr: 'sr-ME',
}

/** Aktif dilin Intl uyumlu etiketini döndürür (tarih/sayı biçimleme için). */
export function getIntlLocale(): string {
  return BCP47[getCurrentLocale()]
}

/** HTML lang özniteliği için BCP-47 karşılıkları. */
const HTML_LANG: Record<AppLocale, string> = {
  tr: 'tr',
  en: 'en',
  cnr: 'cnr',
}

export function isSupportedLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/** Kayıtlı seçim → tarayıcı dili → varsayılan sırasıyla başlangıç dilini belirler. */
export function resolveInitialLocale(): AppLocale {
  if (!isI18nEnabled) return DEFAULT_LOCALE
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLocale(stored)) return stored
  } catch {
    /* localStorage erişilemiyorsa varsayılana düş */
  }
  const browser = (navigator.language || '').toLowerCase()
  if (browser.startsWith('en')) return 'en'
  if (browser.startsWith('cnr') || browser.startsWith('sr') || browser.startsWith('me')) return 'cnr'
  return DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: resolveInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { tr, en, cnr },
  // Eksik çeviriler konsolu doldurmasın; fallback (tr) devreye girer.
  missingWarn: false,
  fallbackWarn: false,
})

/** Aktif dili döndürür (i18n dışından, ör. axios interceptor için). */
export function getCurrentLocale(): AppLocale {
  const current = i18n.global.locale.value
  return isSupportedLocale(current) ? current : DEFAULT_LOCALE
}

/**
 * Uygulama dilini değiştirir; vue-i18n, dayjs, <html lang> ve kalıcılığı
 * birlikte günceller. PrimeVue locale'i App tarafında bu değere tepki verir.
 */
export function setLocale(locale: AppLocale): void {
  if (!isSupportedLocale(locale)) return
  i18n.global.locale.value = locale
  dayjs.locale(DAYJS_LOCALE[locale])
  document.documentElement.setAttribute('lang', HTML_LANG[locale])
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* kalıcılık yoksa oturum boyunca çalışmaya devam et */
  }
}

/** Uygulama açılışında dil yan etkilerini (dayjs, html lang) uygular. */
export function initLocaleSideEffects(): void {
  const locale = getCurrentLocale()
  dayjs.locale(DAYJS_LOCALE[locale])
  document.documentElement.setAttribute('lang', HTML_LANG[locale])
}

export default i18n
